<script setup>
import { useUserStore } from '~/store/user'
import Header from '~/components/airdrop/header.vue'
import DropItem from '~/components/airdrop/dropItem.vue'
import xMod from '@/components/common/xMod.vue'
import xCheckbox from '@/components/common/xCheckbox.vue'
import NoData from '@/components/common/NoData.vue'
import WalletDownloadModal from '@/components/common/WalletDownloadModal.vue'
import { inApp } from '@/utils/inApp'
import { getAirdropList, getAirdropNum, getAirdropTotal } from '@/apis/airdrop'
import { callHandler } from '@/utils/JSbridge'
import { useI18n, useLocalePath } from '#imports'

const localePath = useLocalePath()
const user = useUserStore()

const dropTotal = ref({
  totalUserCount: 0, // 参与用户总数
  totalAirdropCount: 0, // 总空投数量
  totalAirdropValue: 0, // 总空投USDT价值
  candyCount: 0, // 我的糖果数量
  candyCountCanUse: 0, // 有效糖果数量
})

const dropCount = ref({
  allCount: 0, // 全部
  ongoingCount: 0, // 进行中
  upcomingCount: 0, // 即将开始
  endedCount: 0, // 已结束
  myJoinedCount: 0, // 我参与的
})

const dropList = ref([])
const tokenValue = ref('')

definePageMeta({
  stickyHeader: true,
  supportMobile: true,
})

const { t } = useI18n()
const showMyActive = ref(false)
const curTab = ref('') // 0: 即将开始 1: 进行中 2: 已结束
const loading = ref(true)
const loadingTimer = ref(null)

// 监听loading状态  10秒后仍然是loading 则刷新页面
import.meta.client && watchEffect((onCleanup) => {
  if (loading.value) {
    loadingTimer.value = setTimeout(() => {
      if (loading.value) {
        window.location.reload()
      }
    }, 10000)
    onCleanup(() => {
      if (loadingTimer.value) {
        clearTimeout(loadingTimer.value)
        loadingTimer.value = null
      }
    })
  }
})

const TAB_CONFIG = [
  { title: t('全部'), key: '', countKey: 'allCount' },
  { title: t('进行中'), key: 1, countKey: 'ongoingCount' },
  { title: t('即将开始'), key: 0, countKey: 'upcomingCount' },
  { title: t('已结束'), key: 2, countKey: 'endedCount' },
]

const tabs = computed(() => {
  return TAB_CONFIG.map(config => ({
    title: `${config.title}(${dropCount.value[config.countKey]})`,
    key: config.key,
    count: dropCount.value[config.countKey],
  }))
})

function getParams() {
  return {
    status: curTab.value,
    joined: showMyActive.value ? 1 : 0,
  }
}

// 获取空投列表数据
async function getList() {
  const result = await getAirdropList(user.isLogin, getParams())
  if (result.success) {
    dropList.value = result.data
  }
  loading.value = false
}

// 获取空投数量统计
async function fetchAirdropCount() {
  const { success, data } = await getAirdropNum(user.isLogin, getParams())
  if (success)
    dropCount.value = data
}

watch([curTab, showMyActive], () => {
  getList()
  if (showMyActive.value !== undefined) {
    fetchAirdropCount()
  }
})

// 获取空投总数据
async function fetchAirdropTotal() {
  const { success, data } = await getAirdropTotal(user.isLogin)
  if (success)
    dropTotal.value = data
}

function initData() {
  fetchAirdropTotal()
  fetchAirdropCount()
  getList()
}

if (import.meta.client && inApp) {
  // 设置标题
  callHandler({
    uri: 'window',
    method: 'nativeSetTitle',
    data: { title: t('空投') },
  })
}

watch(() => user.isLogin, async () => {
  if (import.meta.server)
    return
  initData()
}, { immediate: true })

// 对活动进行分组展示
const groupedDropList = computed(() => {
  return {
    ongoing: dropList.value.filter(item => item.projectStatus === 1),
    upcoming: dropList.value.filter(item => item.projectStatus === 0),
    ended: dropList.value.filter(item => item.projectStatus === 2),
  }
})
</script>

<template>
  <Header :drop-total="dropTotal" :is-login="user.isLogin" />

  <div class="w-[1200px] mx-auto pt-16 pb-8 max-md:w-full max-md:p-4">
    <div class="flex flex-col gap-8 max-md:gap-2">
      <div class="flex items-center justify-between">
        <h2 class="text-[32px] font-bold max-md:text-xl">
          {{ t('项目列表') }}
        </h2>
        <a
          :href="`${t('zendesk')}/articles/45942456596633-CTC%E7%A9%BA%E6%8A%95%E6%8C%96%E7%9F%BF%E6%B4%BB%E5%8A%A8%E5%8F%82%E4%B8%8E%E6%95%99%E7%A8%8B`"
          class="flex items-center gap-1 text-base text-textPrimary max-md:text-sm"
          target="_blank"
        >
          <svgo-airdrop-ic-futures-account class="w-4 h-4" />
          {{ t('查看教程') }}
        </a>
      </div>

      <div class="flex items-center justify-between max-md:flex-col max-md:items-start max-md:gap-4">
        <xMod
          v-model="curTab"
          :tabs="tabs"
          header-class="border-transparent text-base !px-0 max-md:h-[28px] max-md:text-sm whitespace-nowrap max-md:overflow-auto max-md:w-[calc(100vw-32px)] [&::-webkit-scrollbar]:hidden"
          @tab-change="(v) => curTab = v"
        />
        <xCheckbox v-if="user.isLogin" v-model="showMyActive" class="py-4 text-textPrimary">
          {{ t('我参与的活动') }}
        </xCheckbox>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-100">
      <CommonIconsLoading class="w-10 h-10" />
    </div>

    <template v-else>
      <div v-if="dropList?.length" class="flex flex-col gap-10 pt-10 max-md:pt-6">
        <template v-for="(items, status) in groupedDropList" :key="status">
          <div v-if="items.length">
            <h2 class="pb-4 text-2xl font-bold text-textPrimary max-md:text-lg">
              {{ status === 'ongoing' ? t('进行中') : status === 'upcoming' ? t('即将开始') : t('已结束') }} ({{ items.length }})
            </h2>
            <div class="flex flex-col gap-4">
              <DropItem
                v-for="item in items"
                :key="item.id"
                :item-info="item"
                v-bind="item"
              />
            </div>
          </div>
        </template>
      </div>
      <NoData v-else />
    </template>
    <WalletDownloadModal
      :show-remind="user.isLogin ? true : false"
    />
  </div>
</template>
